import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import BusinessContact from '../../../../../components/common/Cards/BusinessContact';
import { formatTeamMember, hasValidManagerInfo } from '../../../../../utils/salesforceParser';
import { useTrans } from '../../../../../services/i18n';
import Typography from '../../../../../components/common/Typography';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    marginTop: '20px',
  },
  cardsWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fallbackCard: {
    width: '100%',
    borderRadius: 12,
    border: 'solid 1px rgba(136, 143, 169, 0.2)',
    backgroundColor: '#f8f9fa',
    margin: '35px 20px 20px 0',
    padding: '23px 30px',
    textAlign: 'center',
  },
  fallbackTitle: {
    fontSize: 16,
    marginBottom: 10,
    fontWeight: 'bold',
  },
  fallbackMessage: {
    color: '#495057',
    fontSize: 14,
    lineHeight: 1.5,
  },
  fallbackPhone: {
    color: '#0056b3',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const FALLBACK_PHONE = '(877) 252-2700';

function FallbackContact({ type, title }) {
  const classes = useStyles();
  const { trans } = useTrans();

  return (
    <div className={classes.fallbackCard}>
      <Typography className={classes.fallbackTitle}>{title}</Typography>
      <Typography className={classes.fallbackMessage}>
        {trans('contact-your')} {title}:{' '}
        <a href={`tel:${FALLBACK_PHONE.replace(/[^0-9]/g, '')}`} className={classes.fallbackPhone}>
          {FALLBACK_PHONE}
        </a>
      </Typography>
    </div>
  );
}

/**
 * Component to display USFCR Team contacts (Case Manager and Registration & Contracting Specialist)
 * @param {Object} subscription - Subscription data containing Salesforce fields
 */
function USFCRTeamContacts({ subscription }) {
  const classes = useStyles();
  const { trans } = useTrans();

  if (!subscription) {
    return null;
  }

  // Parse both team members from Salesforce data
  const caseManager = formatTeamMember('case-manager', subscription.case_manager_info__c);
  const registrationSpecialist = formatTeamMember(
    'registration-contracting-specialist',
    subscription.account_manager_info__c
  );

  const hasCaseManager = hasValidManagerInfo(caseManager.person);
  const hasSpecialist = hasValidManagerInfo(registrationSpecialist.person);

  // If neither exists, don't render anything
  if (!hasCaseManager && !hasSpecialist) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.cardsWrapper}>
        {/* Case Manager Card */}
        {hasCaseManager ? (
          <BusinessContact item={caseManager} makeContactLink />
        ) : (
          <FallbackContact type="case-manager" title={trans('case-manager')} />
        )}

        {/* Registration & Contracting Specialist Card */}
        {hasSpecialist ? (
          <BusinessContact item={registrationSpecialist} makeContactLink />
        ) : (
          <FallbackContact
            type="registration-contracting-specialist"
            title={trans('registration-contracting-specialist')}
          />
        )}
      </div>
    </div>
  );
}

export default USFCRTeamContacts;
